import { CommercialCMSService } from 'CommonData/services/commercialCMS.service';
import { ProductSynopses, ProductTexture } from "CommonData/poco";
import { ZipService } from "CommonData/services/zip.service";
import { LoadingHelper } from "CommonScripts/loadingHelper";
import { getCurrentProductSynopses, matchingProductTexture } from "./textureSelect";
import { LayingPatternViewer } from "CommonScripts/layingPatternViewer";
import { VisualisationGatewayService } from 'CommonData/services/visualisationGateway.service';

const customPatternContentEl: Element = document.getElementsByClassName('js-commercial-cta-custom-pattern-content')[0];
const downloadContentEl: Element = document.getElementsByClassName('js-commercial-cta-download-content')[0];
const commercialCTAEl: Element = document.getElementsByClassName('js-commercial-cta')[0];
const basePlanSizeEl: Element = document.getElementsByClassName('js-commercial-cta-custom-pattern-item')[0]?.cloneNode(true) as HTMLElement;

export class CommercialCTA {
    public static init() {
        this.setEventListeners();
    }

    public static setEventListeners() {
        const customPatternButtonEl: HTMLButtonElement = document.getElementsByClassName('js-commercial-cta-custom-pattern')[0] as HTMLButtonElement;
        const downloadOptionEls: Array<HTMLInputElement> = [].slice.call(document.getElementsByClassName('js-commercial-asset-download-option'));
        const downloadButtonEl: HTMLButtonElement = document.getElementsByClassName('js-commercial-cta-download-button')[0] as HTMLButtonElement;
        const backButtonEls: Array<Element> = [].slice.call(document.getElementsByClassName('js-commercial-cta-back'));
        const assetFormEl: HTMLFormElement = document.getElementsByClassName('js-commercial-cta-asset-form')[0] as HTMLFormElement;
        const ctaButtonEls: Array<HTMLButtonElement> = [].slice.call(document.getElementsByClassName('js-commercial-cta-request'));
        const customPatternFormEl: HTMLFormElement = document.getElementsByClassName('js-commercial-cta-custom-pattern-form')[0] as HTMLFormElement;
        const addPlanSizeButtonEl: HTMLButtonElement = document.getElementsByClassName('js-commercial-cta-custom-pattern-add')[0] as HTMLButtonElement;

        addPlanSizeButtonEl.addEventListener('click', () => {
            this.onAddPlanSizeClick();
        });

        downloadButtonEl.addEventListener('click', () => {
            this.onDownloadButtonClick();
        });

        customPatternFormEl.addEventListener('submit', (e: Event) => {
            this.onCustomPatternSubmit(e);
        });

        customPatternButtonEl.addEventListener('click', () => {
            this.onCustomPatternButtonClick();
        });

        backButtonEls.forEach((backEl: Element) => {
            backEl.addEventListener('click', () => {
                this.onBackClick();
            });
        });

        assetFormEl.addEventListener('submit', (e: Event) => {
            this.onAssetSubmit(e);
        });

        ctaButtonEls.forEach((buttonEl: HTMLButtonElement) => {
            buttonEl.addEventListener('click', () => {
                this.submit(buttonEl);
            });
        });

        downloadOptionEls.forEach((optionEl: HTMLInputElement) => {
            optionEl.addEventListener('change', () => {
                this.setDownloadButtonState();
            });
        });
    }

    public static async setCommercialDownloadOptions() {
        const ctaButtonEls: Array<HTMLButtonElement> = [].slice.call(document.getElementsByClassName('js-commercial-cta-request'));
        const sampleButtonEl: HTMLButtonElement = ctaButtonEls.find((ctaEl: HTMLButtonElement) => ctaEl.getAttribute('data-cta')?.toLowerCase() === 'sample');
        const quoteButtonEl: HTMLButtonElement = ctaButtonEls.find((ctaEl: HTMLButtonElement) => ctaEl.getAttribute('data-cta')?.toLowerCase() === 'quote');
        const downloadOptionEls: Array<HTMLInputElement> = [].slice.call(document.getElementsByClassName('js-commercial-asset-download-option'));
        const currentProductSynopses: ProductSynopses = getCurrentProductSynopses();
        const currentTexture: any = matchingProductTexture;

        const disableElement = (el: HTMLInputElement) => {
            el.parentElement.classList.add('disabled');
            el.setAttribute('disabled', 'true');
        };

        const enableElement = (el: HTMLInputElement) => {
            el.parentElement.classList.remove('disabled');
            el.removeAttribute('disabled');
        };

        const datasheetOptionEl: HTMLInputElement = downloadOptionEls.find((el: HTMLElement) => el.getAttribute('data-download-option') === DownloadTypes.DataSheet);
        const bimOptionEl: HTMLInputElement = downloadOptionEls.find((el: HTMLElement) => el.getAttribute('data-download-option') === DownloadTypes.BIM);
        const epdOptionEl: HTMLInputElement = downloadOptionEls.find((el: HTMLElement) => el.getAttribute('data-download-option') === DownloadTypes.EPD);
        const pbrOptionEl: HTMLInputElement = downloadOptionEls.find((el: HTMLElement) => el.getAttribute('data-download-option') === DownloadTypes.PBR);

        datasheetOptionEl.checked = false;
        bimOptionEl.checked = false;
        epdOptionEl.checked = false;
        pbrOptionEl.checked = false;

        this.setDownloadButtonState();

        if (!currentProductSynopses || !currentTexture || !downloadOptionEls || !downloadOptionEls.length) {
            disableElement(datasheetOptionEl);
            disableElement(bimOptionEl);
            disableElement(epdOptionEl);
            disableElement(pbrOptionEl);

            return;
        }

        if (!currentProductSynopses.datasheetDocumentUrls || !currentProductSynopses.datasheetDocumentUrls.length) {
            disableElement(datasheetOptionEl);
        } else {
            enableElement(datasheetOptionEl);
        }

        if (!currentProductSynopses.bimUrls || !currentProductSynopses.bimUrls.length) {
            disableElement(bimOptionEl);
        } else {
            enableElement(bimOptionEl);
        }

        if (!currentProductSynopses.epdDocumentUrls || !currentProductSynopses.epdDocumentUrls.length) {
            disableElement(epdOptionEl);
        } else {
            enableElement(epdOptionEl);
        }

        if (!currentTexture.textureKey) {
            disableElement(pbrOptionEl);
        } else {
            enableElement(pbrOptionEl);
        }

        if (!currentTexture.commercialSampleSku) {
            sampleButtonEl?.classList.add('disabled');
            sampleButtonEl?.setAttribute('disabled', 'true');
        } else {
            sampleButtonEl?.classList.remove('disabled');
            sampleButtonEl?.removeAttribute('disabled');
        }

        if (!currentProductSynopses.commercialQuoteSku) {
            quoteButtonEl?.classList.add('disabled');
            quoteButtonEl?.setAttribute('disabled', 'true');
        } else {
            quoteButtonEl?.classList.remove('disabled');
            quoteButtonEl?.removeAttribute('disabled');
        }
    }

    public static setCommercialCustomPatternForm() {
        const productNameInputEl: HTMLInputElement = document.getElementById('custom-pattern-form-product') as HTMLInputElement;
        productNameInputEl.value = matchingProductTexture.name;
    }

    private static onDownloadButtonClick() {
        downloadContentEl.classList.remove('hidden');
        commercialCTAEl.classList.add('hidden');
    }

    private static onCustomPatternButtonClick() {
        customPatternContentEl.classList.remove('hidden');
        commercialCTAEl.classList.add('hidden');
    }

    private static onBackClick() {
        downloadContentEl.classList.add('hidden');
        customPatternContentEl.classList.add('hidden');
        commercialCTAEl.classList.remove('hidden');
    }

    private static onAddPlanSizeClick() {
        const newPlanSizeEl: HTMLElement = basePlanSizeEl?.cloneNode(true) as HTMLElement;
        const deletePlanSizeEl: Element = newPlanSizeEl.getElementsByClassName('js-commercial-cta-custom-pattern-item-remove')[0];

        deletePlanSizeEl.addEventListener('click', () => {
            this.onDeletePlanSizeClick(newPlanSizeEl);
        });

        document.getElementsByClassName('js-commercial-cta-custom-pattern-item-list')[0]?.appendChild(newPlanSizeEl);
    }

    private static onDeletePlanSizeClick(planSizeEl: Element) {
        planSizeEl.remove();
    }

    private static async onCustomPatternSubmit(e: Event) {
        e.preventDefault();    

        const productName: string = (document.getElementById('custom-pattern-form-product') as HTMLInputElement)?.value;
        const emailAddress: string = (document.getElementById('custom-pattern-form-email') as HTMLInputElement)?.value;
        const projectM2: string = (document.getElementById('custom-pattern-form-size') as HTMLInputElement)?.value;  
        const layoutTypeEl: HTMLSelectElement = document.getElementById('custom-pattern-form-layout') as HTMLSelectElement;
        const layoutType: string = layoutTypeEl.options[layoutTypeEl.selectedIndex].text;
        let planSizes: Array<string> = [];

        const planSizeEls: Array<Element> = [].slice.call(document.getElementsByClassName('js-commercial-cta-custom-pattern-item'));
        planSizeEls.forEach((planSizeEl: Element) => {
            const length: string = (planSizeEl.getElementsByClassName('js-commercial-cta-custom-pattern-item-length')[0] as HTMLInputElement)?.value;
            const width: string = (planSizeEl.getElementsByClassName('js-commercial-cta-custom-pattern-item-width')[0] as HTMLInputElement)?.value;

            if (length && width) {
                planSizes.push(`${length}x${width}`);
            }
        });

        try {
            LoadingHelper.start();
            await VisualisationGatewayService.postCustomPatternData({
                EmailAddress: emailAddress,
                ProductName: productName,
                PlanSizes: planSizes,
                M2: projectM2,
                LayoutType: layoutType
            });
        } finally {
            LoadingHelper.stop();
            this.onBackClick();
        }
    }

    private static async onAssetSubmit(e: Event) {
        e.preventDefault();

        const formEl: HTMLFormElement = document.getElementsByClassName('js-commercial-cta-asset-form')[0] as HTMLFormElement;
        const projectTypeSelectEl: HTMLSelectElement = formEl.getElementsByTagName("select")[0];
        const projectType: string = projectTypeSelectEl.options[projectTypeSelectEl.selectedIndex].text;
        const emailAddress: string = formEl.getElementsByTagName("input")[0].value;
        const projectTitle: string = formEl.getElementsByTagName("input")[1].value;
        const downloadOptionEls: Array<HTMLInputElement> = [].slice.call(document.getElementsByClassName('js-commercial-asset-download-option'));
        const selectedDownloadOptionEls: Array<HTMLInputElement> = downloadOptionEls?.filter((el: HTMLInputElement) => el.checked);
        const selectedDownloadOptions: Array<string> = selectedDownloadOptionEls.map((el: HTMLInputElement) => el.getAttribute('data-download-option'));

        const currentProductSynopses: ProductSynopses = getCurrentProductSynopses();
        const currentTexture: any = LayingPatternViewer.getCurrentTexture();

        if (!currentProductSynopses || !currentTexture) {
            console.error('Unable to get required product data in commercialCTA.onAssetSubmit. Product: ',currentProductSynopses, 'Texture: ', currentTexture);
        }

        let downloadUrls: Array<string> = [];

        if (selectedDownloadOptions.find((o: string) => o === DownloadTypes.DataSheet) && currentProductSynopses.datasheetDocumentUrls) {
            currentProductSynopses.datasheetDocumentUrls.forEach((dsUrl: string) => {
                downloadUrls.push(dsUrl);
            });
        }

        if (selectedDownloadOptions.find((o: string) => o === DownloadTypes.BIM) && currentProductSynopses.bimUrls) {
            currentProductSynopses.bimUrls.forEach((url: string) => {
                downloadUrls.push(url);
            });
        }

        if (selectedDownloadOptions.find((o: string) => o === DownloadTypes.EPD) && currentProductSynopses.epdDocumentUrls) {
            currentProductSynopses.epdDocumentUrls.forEach((url: string) => {
                downloadUrls.push(url);
            });
        }

        if (selectedDownloadOptions.find((o: string) => o === DownloadTypes.PDF)) {
            downloadUrls.push();
        }

        if (selectedDownloadOptions.find((o: string) => o === DownloadTypes.PBR)) {
            const currentTextureObject: any = LayingPatternViewer.getCurrentTexture();
            const diffuseUrl: string =
                `https://media.marshalls.co.uk/image/upload/VisualiserMaterials/Textures_Jointing/${currentTextureObject.ProductTextureKey}/${currentTextureObject.JointWidthMM}_${currentTextureObject.JointTextureKey}_${currentTextureObject.MaskId}_${currentTextureObject.PatternId}_diffuse_med.jpg`;
            const normalUrl: string = diffuseUrl.replace("_diffuse_med.jpg", "_normal_med.png");
            const roughnessUrl: string = diffuseUrl.replace("_diffuse_", "_roughness_");

            downloadUrls.push(diffuseUrl);
            downloadUrls.push(normalUrl);
            downloadUrls.push(roughnessUrl);
        }

        if (downloadUrls && downloadUrls.length) {
            try {
                LoadingHelper.start();
                await ZipService.downloadAndZip(downloadUrls);
            } finally {
                VisualisationGatewayService.postCommercialDownloadData({
                    EmailAddress: emailAddress,
                    ProjectType: projectType,
                    ProjectTitle: projectTitle,
                    MtlKey: currentTexture.ProductTextureKey,
                    JntKey: `${currentTexture.JointWidthMM}_${currentTexture.JointTextureKey}_${currentTexture.MaskId}_${currentTexture.PatternId}`,
                    PtnId: currentTexture.PatternId
                });

                LoadingHelper.stop();
            }
        }
    }

    private static async submit(el: HTMLButtonElement) {
        let submitType: string = el.getAttribute('data-cta');

        if (!submitType) {
            console.error('Unable to find Commercial CTA submit type: CommercialCTA.submit');
            return;
        }

        try {
            LoadingHelper.start();

            const productTexture: ProductTexture = matchingProductTexture;
            const currentProductSynopses: ProductSynopses = getCurrentProductSynopses();
            const response: any = await CommercialCMSService.postCommercialCTA({
                CTAAction: submitType,
                ProductSlug: currentProductSynopses.websiteUrl,
                SampleId: productTexture.commercialSampleSku,
                VariantId: currentProductSynopses.commercialQuoteSku,
            });

            setTimeout(() => {
                window.open(`${process.env.COMMERCIAL_CMS_URL}/process-cta?${response.data}`);
            }, 0);
        } finally {
            LoadingHelper.stop();
        }
    }

    private static setDownloadButtonState() {
        const downloadOptionEls: Array<HTMLInputElement> = [].slice.call(document.getElementsByClassName('js-commercial-asset-download-option'));
        const downloadButtonEl: HTMLButtonElement = document.getElementsByClassName('js-commercial-cta-download-button')[0] as HTMLButtonElement;
        const selectedOptions: Array<HTMLInputElement> = downloadOptionEls.filter((oEl: HTMLInputElement) => oEl.checked);

        if (selectedOptions && selectedOptions.length) {
            downloadButtonEl.removeAttribute('disabled');
            downloadButtonEl.classList.remove('disabled');
        } else {
            downloadButtonEl.setAttribute('disabled', 'true');
            downloadButtonEl.classList.add('disabled');
        }
    }
}

enum DownloadTypes {
    DataSheet = 'datasheet',
    PDF = 'pdf',
    BIM = 'bim',
    PBR = 'pbr',
    EPD = 'epd'
}

CommercialCTA.init();